import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import './order.css';
import ClipLoader from 'react-spinners/ClipLoader';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
const ORDERS_PER_PAGE = 25;

const getAddress = (order) =>
    order.shipping_address || order.billing_address || order.customer?.default_address || {};

const getLineItems = (order) => (Array.isArray(order.line_items) ? order.line_items : []);

function DrmtlgyOrdersTable() {
    const [allOrders, setAllOrders] = useState([]);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [fetchError, setFetchError] = useState('');
    const [hasMore, setHasMore] = useState(false);
    const [totalOrders, setTotalOrders] = useState(0);

    const [viewMode, setViewMode] = useState('lite');
    const [search, setSearch] = useState('');
    const [financialStatus, setFinancialStatus] = useState('all');
    const [fulfillmentStatus, setFulfillmentStatus] = useState('all');
    const [locationIdFilter, setLocationIdFilter] = useState('all');
    const [onlyUnfulfilledLines, setOnlyUnfulfilledLines] = useState(false);

    const [lookupOrderNumber, setLookupOrderNumber] = useState('');
    const [lookupLoading, setLookupLoading] = useState(false);
    const [lookupResult, setLookupResult] = useState(null);
    const [lookupFulfillmentGroups, setLookupFulfillmentGroups] = useState([]);
    const [lookupRawPayload, setLookupRawPayload] = useState(null);
    const [lookupError, setLookupError] = useState('');
    const [lookupCollapsed, setLookupCollapsed] = useState(false);
    const [lookupActiveTab, setLookupActiveTab] = useState('locations');

    const fetchAuthHeaders = () => {
        const token = localStorage.getItem('adminToken');
        return token ? { Authorization: `Bearer ${token}` } : {};
    };

    const fetchAllOrders = async (pageNum = 1) => {
        try {
            pageNum === 1 ? setLoading(true) : setLoadingMore(true);
            if (pageNum === 1) setFetchError('');

            const res = await axios.get(`${API_BASE_URL}/api/drmtlgy/shopify/orders`, {
                headers: fetchAuthHeaders(),
                params: { 
                    from: fromDate, 
                    to: toDate,
                    limit: 500,
                    offset: (pageNum - 1) * 500
                },
                timeout: 60000,
            });

            const newOrders = res.data.orders || [];
            const total = res.data.total || 0;

            if (pageNum === 1) {
                setAllOrders(newOrders);
            } else {
                setAllOrders(prev => [...prev, ...newOrders]);
            }

            setTotalOrders(total);
            setHasMore(newOrders.length === 500); // Has more if returned full page
            setCurrentPage(pageNum);
        } catch (err) {
            console.error('Error fetching orders:', err);

            let msg = 'Unknown error';
            if (err.code === 'ECONNABORTED') {
                msg = 'Request timeout - the server is taking too long to respond. Try a smaller date range.';
            } else if (err.response?.status === 429) {
                msg = 'Too many requests - please wait a moment and try again.';
            } else if (err.response?.status === 500) {
                msg = 'Server error - please try again later.';
            } else {
                msg = err.response?.data?.details || err.response?.data?.error || err.message || 'Unknown error';
            }

            if (pageNum === 1) {
                setFetchError(`Failed to fetch orders: ${msg}`);
                setAllOrders([]);
            } else {
                setFetchError(`Failed to load more orders: ${msg}`);
            }
        } finally {
            pageNum === 1 ? setLoading(false) : setLoadingMore(false);
        }
    };

    useEffect(() => {
        if (fromDate && toDate) {
            if (fromDate > toDate) {
                setFetchError('From date must be before or equal to To date.');
                setAllOrders([]);
                return;
            }

            // Debounce: wait 500ms after date change before fetching
            const timer = setTimeout(() => {
                fetchAllOrders(1);
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [fromDate, toDate]);

    const locationOptions = useMemo(() => {
        const values = new Set();
        allOrders.forEach((order) => {
            getLineItems(order).forEach((item) => {
                if (item?.location_id) values.add(String(item.location_id));
            });
        });
        return Array.from(values).sort();
    }, [allOrders]);

    const financialStatuses = useMemo(
        () =>
            Array.from(
                new Set(allOrders.map((order) => String(order.financial_status || '').toLowerCase()).filter(Boolean))
            ),
        [allOrders]
    );

    const fulfillmentStatuses = useMemo(
        () =>
            Array.from(
                new Set(
                    allOrders
                        .map((order) => String(order.fulfillment_status || 'unfulfilled').toLowerCase())
                        .filter(Boolean)
                )
            ),
        [allOrders]
    );

    const filteredOrders = useMemo(() => {
        const q = search.trim().toLowerCase();
        return allOrders.filter((order) => {
            const address = getAddress(order);
            const items = getLineItems(order);
            const haystack = [
                order.order_number,
                order.name,
                order.customer?.first_name,
                order.customer?.last_name,
                address.city,
                ...(items || []).map((item) => `${item?.name || ''} ${item?.sku || ''} ${item?.location_id || ''}`),
            ]
                .join(' ')
                .toLowerCase();

            if (q && !haystack.includes(q)) return false;
            if (
                financialStatus !== 'all' &&
                String(order.financial_status || '').toLowerCase() !== financialStatus
            ) {
                return false;
            }
            if (
                fulfillmentStatus !== 'all' &&
                String(order.fulfillment_status || 'unfulfilled').toLowerCase() !== fulfillmentStatus
            ) {
                return false;
            }
            if (locationIdFilter !== 'all') {
                const hasLocation = items.some((item) => String(item?.location_id || '') === locationIdFilter);
                if (!hasLocation) return false;
            }
            if (onlyUnfulfilledLines) {
                const hasUnfulfilled = items.some((item) => Number(item?.fulfillable_quantity || 0) > 0);
                if (!hasUnfulfilled) return false;
            }

            return true;
        });
    }, [allOrders, search, financialStatus, fulfillmentStatus, locationIdFilter, onlyUnfulfilledLines]);

    const totalPages = Math.max(1, Math.ceil(filteredOrders.length / ORDERS_PER_PAGE));
    const pageOrders = useMemo(() => {
        const startIndex = (currentPage - 1) * ORDERS_PER_PAGE;
        return filteredOrders.slice(startIndex, startIndex + ORDERS_PER_PAGE);
    }, [filteredOrders, currentPage]);

    useEffect(() => {
        if (currentPage > totalPages && totalPages > 0) setCurrentPage(1);
    }, [currentPage, totalPages]);

    const handleOrderLookup = async (e) => {
        e.preventDefault();
        if (!lookupOrderNumber.trim()) return;
        setLookupLoading(true);
        setLookupResult(null);
        setLookupFulfillmentGroups([]);
        setLookupRawPayload(null);
        setLookupError('');
        setLookupCollapsed(false);
        setLookupActiveTab('locations');
        try {
            const res = await axios.get(
                `${API_BASE_URL}/api/drmtlgy/order-lookup/${encodeURIComponent(lookupOrderNumber.trim())}`,
                {
                    headers: fetchAuthHeaders(),
                }
            );
            const payload = res.data || {};
            setLookupRawPayload(payload);
            setLookupResult(payload.order || null);
            setLookupFulfillmentGroups(
                payload.unfulfilledAssignedLocations?.groups || payload.fulfillmentGroups || []
            );
        } catch (err) {
            setLookupError(err.response?.data?.error || 'Failed to fetch order.');
        } finally {
            setLookupLoading(false);
        }
    };

    const clearLookup = () => {
        setLookupOrderNumber('');
        setLookupResult(null);
        setLookupFulfillmentGroups([]);
        setLookupRawPayload(null);
        setLookupError('');
    };

    const exportToCSV = () => {
        if (!filteredOrders.length) return;
        const headers = [
            'Order ID',
            'Order Number',
            'Customer',
            'Location IDs',
            'Fulfillable Qty',
            'Financial Status',
            'Fulfillment Status',
            'Order Total',
        ];

        const rows = filteredOrders.map((order) => {
            const items = getLineItems(order);
            const locationIds = Array.from(new Set(items.map((item) => item?.location_id).filter(Boolean))).join('|');
            const fulfillableQty = items.reduce((sum, item) => sum + Number(item?.fulfillable_quantity || 0), 0);
            return [
                order.id,
                order.order_number || '',
                `${order.customer?.first_name || ''} ${order.customer?.last_name || ''}`.trim(),
                locationIds,
                fulfillableQty,
                order.financial_status || '',
                order.fulfillment_status || 'unfulfilled',
                order.total_price || 0,
            ];
        });

        const csvContent = [
            headers.join(','),
            ...rows.map((row) => row.map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(',')),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `drmtlgy_shopify_orders_${Date.now()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;
        const maxPageButtons = 10;
        const currentBatchStart = Math.floor((currentPage - 1) / maxPageButtons) * maxPageButtons + 1;
        const currentBatchEnd = Math.min(currentBatchStart + maxPageButtons - 1, totalPages);
        const pageNumbers = [];
        for (let i = currentBatchStart; i <= currentBatchEnd; i++) pageNumbers.push(i);

        return (
            <nav>
                <ul className="pagination justify-content-center mt-3">
                    <li className={`page-item ${currentBatchStart === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => setCurrentPage(currentBatchStart - 1)}
                            disabled={currentBatchStart === 1}
                        >
                            &laquo;
                        </button>
                    </li>
                    {pageNumbers.map((num) => (
                        <li key={num} className={`page-item ${num === currentPage ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(num)}>
                                {num}
                            </button>
                        </li>
                    ))}
                    <li className={`page-item ${currentBatchEnd === totalPages ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => setCurrentPage(currentBatchEnd + 1)}
                            disabled={currentBatchEnd === totalPages}
                        >
                            &raquo;
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

    const renderLoadMore = () => {
        return (
            <div className="d-flex justify-content-center align-items-center mt-4 mb-4">
                <div className="text-center">
                    <p className="text-muted">
                        Showing <strong>{allOrders.length}</strong> of <strong>{totalOrders}</strong> orders
                    </p>
                    {hasMore && (
                        <button
                            className="btn btn-primary"
                            onClick={() => fetchAllOrders(currentPage + 1)}
                            disabled={loadingMore}
                        >
                            {loadingMore ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Loading more orders...
                                </>
                            ) : (
                                'Load More Orders'
                            )}
                        </button>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="orderByDate container mt-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h2 className="mb-0">DRMTLGY Shopify Orders</h2>
                <div className="btn-group view-mode-switch">
                    <button
                        className={`btn btn-sm ${viewMode === 'lite' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('lite')}
                    >
                        Lite View
                    </button>
                    <button
                        className={`btn btn-sm ${viewMode === 'advanced' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('advanced')}
                    >
                        Advanced View
                    </button>
                </div>
            </div>

            <div className="card order-lookup-card mb-4">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <span className="fw-semibold">Order Lookup - View Full API Response</span>
                    {lookupResult && (
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => setLookupCollapsed((c) => !c)}>
                            {lookupCollapsed ? 'Show Response' : 'Collapse'}
                        </button>
                    )}
                </div>
                <div className="card-body">
                    <form className="d-flex gap-2 align-items-end flex-wrap" onSubmit={handleOrderLookup}>
                        <div style={{ flex: '0 0 260px' }}>
                            <label className="form-label mb-1">Order Number</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="e.g. 0212345 or #0212345"
                                value={lookupOrderNumber}
                                onChange={(e) => setLookupOrderNumber(e.target.value)}
                            />
                        </div>
                        <button type="submit" className="btn btn-primary" disabled={lookupLoading || !lookupOrderNumber.trim()}>
                            {lookupLoading ? <><span className="spinner-border spinner-border-sm me-1" />Looking up...</> : 'Get Order'}
                        </button>
                        {(lookupResult || lookupError) && (
                            <button type="button" className="btn btn-outline-danger" onClick={clearLookup}>
                                Clear
                            </button>
                        )}
                    </form>

                    {lookupError && <div className="alert alert-danger mt-3 mb-0 py-2">{lookupError}</div>}

                    {lookupResult && !lookupCollapsed && (
                        <div className="mt-3">
                            <div className="lookup-summary d-flex flex-wrap gap-3 mb-3 p-2 rounded">
                                <span><strong>Order:</strong> {lookupResult.name}</span>
                                <span><strong>ID:</strong> {lookupResult.id}</span>
                                <span><strong>Total:</strong> ${lookupResult.total_price}</span>
                                <span><strong>Financial:</strong> {lookupResult.financial_status}</span>
                                <span><strong>Fulfillment:</strong> {lookupResult.fulfillment_status || 'unfulfilled'}</span>
                                <span><strong>Fulfillment Groups:</strong> {lookupFulfillmentGroups.length}</span>
                            </div>

                            <ul className="nav nav-tabs lookup-tabs mb-3">
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${lookupActiveTab === 'locations' ? 'active' : ''}`}
                                        onClick={() => setLookupActiveTab('locations')}
                                    >
                                        Fulfillment Locations
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${lookupActiveTab === 'raw' ? 'active' : ''}`}
                                        onClick={() => setLookupActiveTab('raw')}
                                    >
                                        Raw API Response
                                    </button>
                                </li>
                            </ul>

                            {lookupActiveTab === 'locations' && (
                                <div>
                                    {lookupFulfillmentGroups.length === 0 ? (
                                        <div className="alert alert-secondary py-2 mb-0">No fulfillment groups found.</div>
                                    ) : (
                                        <div className="fulfillment-groups">
                                            {lookupFulfillmentGroups.map((group) => (
                                                <div key={group.groupIndex} className="fulfillment-group-card mb-3">
                                                    <div className="fulfillment-group-header d-flex align-items-center gap-2 px-3 py-2">
                                                        <span className="fulfillment-group-num">Group {group.groupIndex}</span>
                                                        {group.location ? (
                                                            <>
                                                                <span className="fulfillment-location-name">{group.location.name}</span>
                                                                <span className="badge bg-secondary ms-auto" style={{ fontFamily: 'monospace' }}>
                                                                    ID: {group.location.numericId}
                                                                </span>
                                                            </>
                                                        ) : (
                                                            <span className="text-warning">Location not assigned</span>
                                                        )}
                                                    </div>
                                                    <div className="fulfillment-group-body">
                                                        <table className="table table-sm mb-0 fulfillment-items-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Item</th>
                                                                    <th>SKU</th>
                                                                    <th className="text-center">Qty</th>
                                                                    <th className="text-center">Remaining</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {(group.lineItems || []).map((item, i) => (
                                                                    <tr key={i}>
                                                                        <td>{item.title}</td>
                                                                        <td><code>{item.sku}</code></td>
                                                                        <td className="text-center">{item.quantity}</td>
                                                                        <td className="text-center">{item.remainingQuantity}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {lookupActiveTab === 'raw' && (
                                <div className="lookup-json-wrapper">
                                    <div className="lookup-json-toolbar d-flex justify-content-between align-items-center px-2 py-1">
                                        <span className="text-muted" style={{ fontSize: '0.75rem' }}>Raw Shopify response</span>
                                        <button
                                            className="btn btn-sm btn-outline-secondary py-0"
                                            style={{ fontSize: '0.72rem' }}
                                            onClick={() => navigator.clipboard.writeText(JSON.stringify(lookupRawPayload, null, 2))}
                                        >
                                            Copy JSON
                                        </button>
                                    </div>
                                    <pre className="lookup-json-pre"><code>{JSON.stringify(lookupRawPayload, null, 2)}</code></pre>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="card filter-card mb-4">
                <div className="card-body">
                    <div className="row g-3 align-items-end">
                        <div className="col-md-2">
                            <label className="form-label">From</label>
                            <input type="date" className="form-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">To</label>
                            <input type="date" className="form-control" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Search</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Order, customer, SKU"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Financial</label>
                            <select
                                className="form-select"
                                value={financialStatus}
                                onChange={(e) => setFinancialStatus(e.target.value)}
                            >
                                <option value="all">All</option>
                                {financialStatuses.map((status) => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Fulfillment</label>
                            <select
                                className="form-select"
                                value={fulfillmentStatus}
                                onChange={(e) => setFulfillmentStatus(e.target.value)}
                            >
                                <option value="all">All</option>
                                {fulfillmentStatuses.map((status) => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label">Location ID</label>
                            <select
                                className="form-select"
                                value={locationIdFilter}
                                onChange={(e) => setLocationIdFilter(e.target.value)}
                            >
                                <option value="all">All</option>
                                {locationOptions.map((id) => (
                                    <option key={id} value={id}>{id}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="row g-3 mt-1 align-items-center">
                        <div className="col-md-4">
                            <div className="form-check mt-2">
                                <input
                                    id="unfulfilledOnlyDRM"
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={onlyUnfulfilledLines}
                                    onChange={(e) => setOnlyUnfulfilledLines(e.target.checked)}
                                />
                                <label htmlFor="unfulfilledOnlyDRM" className="form-check-label">
                                    Show orders with fulfillable line items only
                                </label>
                            </div>
                        </div>
                        <div className="col-md-8 d-flex justify-content-end">
                            <button className="btn btn-success" onClick={exportToCSV} disabled={!filteredOrders.length}>
                                Export Filtered CSV
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {fetchError && (
                <div className="alert alert-danger my-3" role="alert">
                    {fetchError}
                </div>
            )}

            {loading ? (
                <div className="text-center my-5">
                    <ClipLoader color="#36d7b7" size={50} />
                    <p className="mt-3">Loading orders... This may take a moment for large date ranges.</p>
                </div>
            ) : (
                <>
                    <div className="dateTable table-responsive">
                        {viewMode === 'lite' ? (
                            <table className="table table-hover table-bordered">
                                <thead className="table-light">
                                    <tr>
                                        <th>Order #</th>
                                        <th>Customer</th>
                                        <th>Location IDs</th>
                                        <th>Items</th>
                                        <th>Fulfillable</th>
                                        <th>Financial</th>
                                        <th>Fulfillment</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageOrders.length ? (
                                        pageOrders.map((order) => {
                                            const items = getLineItems(order);
                                            const locationIds = Array.from(
                                                new Set(items.map((item) => item?.location_id).filter(Boolean))
                                            );
                                            const fulfillableQty = items.reduce(
                                                (sum, item) => sum + Number(item?.fulfillable_quantity || 0),
                                                0
                                            );
                                            return (
                                                <tr key={order.id}>
                                                    <td className="fw-semibold">{order.order_number || '�'}</td>
                                                    <td>{order.customer?.first_name} {order.customer?.last_name}</td>
                                                    <td>{locationIds.length ? locationIds.join(', ') : '�'}</td>
                                                    <td>{items.length}</td>
                                                    <td>{fulfillableQty}</td>
                                                    <td>{order.financial_status || '�'}</td>
                                                    <td>{order.fulfillment_status || 'unfulfilled'}</td>
                                                    <td>${order.total_price || 0}</td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="text-center">No orders found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        ) : (
                            <table className="table table-bordered order-grouped-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Store Order No</th>
                                        <th>Customer Name</th>
                                        <th>City</th>
                                        <th>Created Date</th>
                                        <th>Order Item</th>
                                        <th>SKU</th>
                                        <th>Qty</th>
                                        <th>Location ID</th>
                                        <th>Fulfillable Qty</th>
                                        <th>Item Status</th>
                                        <th>Total</th>
                                        <th>Financial</th>
                                        <th>Fulfillment</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageOrders.length ? (
                                        pageOrders.flatMap((order) => {
                                            const items = getLineItems(order);
                                            const rows = items.length ? items : [null];
                                            const address = getAddress(order);
                                            return rows.map((item, idx) => (
                                                <tr key={`${order.id}-${idx}`}>
                                                    <td>{order.id}</td>
                                                    <td>{order.order_number || '�'}</td>
                                                    <td>{order.customer?.first_name} {order.customer?.last_name}</td>
                                                    <td>{address.city || '�'}</td>
                                                    <td>{order.created_at ? new Date(order.created_at).toLocaleDateString() : '�'}</td>
                                                    <td>{item?.name || '�'}</td>
                                                    <td>{item?.sku || '�'}</td>
                                                    <td>{item?.quantity ?? '�'}</td>
                                                    <td>{item?.location_id ? <span className="badge bg-secondary">{item.location_id}</span> : '�'}</td>
                                                    <td>{item?.fulfillable_quantity ?? 0}</td>
                                                    <td>{item?.fulfillment_status || 'NA'}</td>
                                                    <td>${order.total_price || 0}</td>
                                                    <td>{order.financial_status || '�'}</td>
                                                    <td>{order.fulfillment_status || 'unfulfilled'}</td>
                                                </tr>
                                            ));
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="14" className="text-center">No orders found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {renderPagination()}

                    {renderLoadMore()}

                    <div className="text-center mt-3">
                        <p>
                            Showing <strong>{pageOrders.length}</strong> of <strong>{filteredOrders.length}</strong> filtered orders |
                            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                        </p>
                    </div>
                </>
            )}
        </div>
    );
}

export default DrmtlgyOrdersTable;
